import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import { getCategoriesState } from 'packages/store/categories/categories.selectors';
import { getTemplates } from 'packages/store/templates/templates.selectors';
import { customerIdSelector } from 'packages/store/user/user.selectors';
import { CollectionSubType, CollectionType } from 'packages/store/collections/types';
import { fetchCategories, fetchCategoriesBySearchQuery } from 'packages/store/categories/categories.actions';
import { fetchTemplates, fetchTemplatesBySearchQuery } from 'packages/store/templates/templates.actions';
import debounce from 'packages/helpers/debounce';
import useQueryParams from 'packages/hooks/useQueryParams';
import { CategoriesTab } from 'packages/pages/categories/components/CategoriesTabs/types';
import { MediaFilterType } from '../filters/components/MediaType/types';
import { OrderFilterType, SortingFilterType } from '../filters/components/Ordering/types';
import { OwnershipFilterType } from '../filters/components/Ownership/types';

const type: CollectionType = 'CREATIVE';
const subType: CollectionSubType = 'TEMPLATE';

const useFilters = (activeTab: CategoriesTab) => {
    // if you want to predefine filters value on page load
    // create the filters state here and pass a value as a prop directly to filters
    const dispatch = useThunkDispatch();
    const { setQueryParam } = useQueryParams();

    const { fetchOptions: categoriesFetchOptions } = useSelector(getCategoriesState);
    const { fetchOptions: templatesFetchOptions } = useSelector(getTemplates);
    const currentCompany = useSelector(customerIdSelector);

    const collectionId = templatesFetchOptions.collectionId;
    const isCategoriesTab = activeTab === CategoriesTab.CATEGORIES;

    // Search is scoped to the active tab: on the Categories tab we only search
    // categories, on the Templates tab we only search templates.
    const onSearch = useMemo(
        () =>
            debounce(
                (newSearchValue: string) => {
                    // todo: add unsubscribe from ws for both actions
                    if (isCategoriesTab) {
                        dispatch(fetchCategoriesBySearchQuery(newSearchValue, type, subType, currentCompany));
                    } else {
                        dispatch(fetchTemplatesBySearchQuery(newSearchValue));
                    }

                    setQueryParam('search', newSearchValue);
                },
                1000,
                false,
            ),
        [collectionId, dispatch, isCategoriesTab, currentCompany],
    );

    const onMediaTypeSelect = (mediaType: MediaFilterType) => {
        const templatesNewOptions = { ...templatesFetchOptions };

        if (mediaType === MediaFilterType.ANY) {
            templatesNewOptions.creativeType = undefined;
        } else {
            templatesNewOptions.creativeType = mediaType;
        }

        dispatch(
            fetchTemplates({
                newOptions: templatesNewOptions,
                setSelected: false,
                fetchFromStart: true,
            }),
        );

        setQueryParam('typeTemplate', mediaType.toLowerCase());
    };

    const onSort = (sortBy: SortingFilterType, sortOrder: OrderFilterType) => {
        if (isCategoriesTab) {
            const categoriesNewOptions = { ...categoriesFetchOptions, sort: [sortBy, sortOrder] };

            dispatch(fetchCategories({ ...categoriesNewOptions, type, subType }, currentCompany, true));
        } else {
            const templatesNewOptions = { ...templatesFetchOptions, sort: [sortBy, sortOrder] };

            dispatch(
                fetchTemplates({
                    newOptions: templatesNewOptions,
                    setSelected: false,
                    fetchFromStart: true,
                }),
            );
        }

        setQueryParam('sortedBy', sortBy);
        setQueryParam('sortOrder', sortOrder);
    };

    const onOwnershipSelect = (ownershipSource: OwnershipFilterType) => {
        if (isCategoriesTab) {
            const categoriesNewOptions = { ...categoriesFetchOptions, ownershipSource };

            dispatch(fetchCategories({ ...categoriesNewOptions, type, subType }, currentCompany, true));
        } else {
            const templatesNewOptions = { ...templatesFetchOptions, ownershipSource };

            dispatch(
                fetchTemplates({
                    newOptions: templatesNewOptions,
                    setSelected: false,
                    fetchFromStart: true,
                }),
            );
        }

        setQueryParam('ownership', ownershipSource.toLowerCase());
    };

    return { onSearch, onSort, onOwnershipSelect, onMediaTypeSelect };
};

export default useFilters;
